package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The three states a length budget can be in, and the only place they are decided. */
internal enum class EditorBudgetState { NONE, WITHIN, OVER }

internal fun editorBudgetState(length: Int, maxLength: Int?): EditorBudgetState = when {
    maxLength == null || maxLength <= 0 -> EditorBudgetState.NONE
    length > maxLength -> EditorBudgetState.OVER
    else -> EditorBudgetState.WITHIN
}

/**
 * How much of the budget this text has spent, as a fraction of the track, clamped at full.
 *
 * Over-budget is drawn by the colour changing, not by a bar that grows past its own track: a fill
 * that overflows its container is a rendering bug in every other component in the product, so it
 * cannot also be this one's way of saying something is wrong.
 */
internal fun editorBudgetRatio(length: Int, maxLength: Int?): Double =
    if (maxLength == null || maxLength <= 0) 0.0
    else (length.toDouble() / maxLength.toDouble()).coerceIn(0.0, 1.0)

/**
 * The length budget, under the field it constrains.
 *
 * The count lives in the action bar, at the bottom of the pane and away from the text it measures.
 * That is the wrong place — the eye has to leave the sentence to read a number about the sentence —
 * and it draws `34 / 18` in the same colour and weight as `12 / 18`, so the one state that matters
 * looks exactly like the one that does not.
 *
 * Three states, not two. The third is the common one: most keys carry no budget at all, and the
 * only place that ever said so was the Details panel's `No maximum`. Drawing an empty track and
 * saying it in words is what invites somebody to set a number, which is the behaviour we want; a
 * bare count says nothing and a missing meter says less.
 *
 * The fill is a `transform`, not a width — a compositor property, changed on every keystroke in a
 * field somebody is typing into, and this codebase animates nothing by `width` for that reason.
 * There is no transition on it either: a meter that eases behind the caret reads as lag.
 *
 * Nothing here is announced. Being over the budget is already an inline QA finding with a sentence
 * of its own (`inlineQa`), and a live region repeating the same fact would announce the overrun
 * twice on every keystroke past it.
 */
@Composable
internal fun EditorBudgetMeter(
    value: String,
    maxLength: Int?,
    modifier: Modifier = Modifier,
) {
    val palette = ColorMode.current.toSitePalette()
    val length = value.length
    val state = editorBudgetState(length, maxLength)
    val fill = when (state) {
        EditorBudgetState.OVER -> palette.danger
        else -> palette.accent
    }

    Column(
        modifier
            .fillMaxWidth()
            .gap(Space.XS)
            .attr("data-editor-budget-meter", state.name.lowercase()),
    ) {
        Div(
            Modifier
                .fillMaxWidth()
                .height(4.px)
                .backgroundColor(palette.surfaceSunken)
                // A track with nothing in it still has to be visible, or the no-budget state is
                // indistinguishable from no meter at all — and it is the state most keys are in.
                // `borderStrong`, not `border`: this boundary is the whole graphic when the track
                // is empty, so it is a 3:1 object and not a hairline divider.
                .border(1.px, LineStyle.Solid, palette.borderStrong)
                .borderRadius(Radius.Pill)
                .styleModifier {
                    property("box-sizing", "border-box")
                    property("overflow", "hidden")
                }
                .toAttrs { attr("aria-hidden", "true") },
        ) {
            if (state != EditorBudgetState.NONE) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .height(100.percent)
                        .backgroundColor(fill)
                        .styleModifier {
                            // The edge the text starts at, not the left one. A length meter that
                            // grew from the left in an Arabic interface would fill away from the
                            // first character the translator typed.
                            property("transform-origin", "var(--l-inline-start, left) center")
                            property("transform", "scaleX(${editorBudgetRatio(length, maxLength)})")
                        }
                        .toAttrs(),
                )
            }
        }
        Span(
            Modifier
                .color(if (state == EditorBudgetState.OVER) palette.danger else palette.textSecondary)
                .fontSize(TypeScale.Small)
                .fontWeight(if (state == EditorBudgetState.OVER) 700 else 600)
                .tabularNumbers()
                .toAttrs(),
        ) {
            Text(editorBudgetLabel(length, maxLength))
        }
    }
}

/**
 * The counter, in the wording the design records: `34 / 18 characters` against a budget, and the
 * missing budget said out loud rather than left as a bare count.
 *
 * Counted through [tPlural] rather than a bare noun, because the bare noun drew **"1 characters"**
 * under every field in the editor of a product that sells real plurals — and in the locales this
 * product exists for it is worse than an English typo: Polish needs a third form at five and
 * Japanese needs one, neither of which a suffixed `s` can express.
 *
 * Against a budget the noun agrees with the *maximum*, not the count: `1 / 18 characters` is what a
 * writer produces, because the phrase reads "1 of 18 characters". So the budget is what is counted
 * and the current length is substituted into the form the reader's language chose.
 */
internal fun editorBudgetLabel(length: Int, maxLength: Int?): String =
    when (editorBudgetState(length, maxLength)) {
        EditorBudgetState.NONE -> tPlural(
            "editor.editor_budget_meter.characters_no_maximum",
            length,
            "{count} character · no maximum set",
            "{count} characters · no maximum set",
        )

        // The length is a named argument rather than a `.replace` bolted onto the result. Both
        // spellings work; only one of them is checked. `tPlural` did not take values when this was
        // written, so the placeholder and the thing that filled it sat on different lines with
        // nothing tying them together — which is exactly the shape a rename walks straight past.
        else -> tPlural(
            "editor.editor_budget_meter.characters_of_maximum",
            maxLength ?: 0,
            "{length} / {count} character",
            "{length} / {count} characters",
            "length" to length,
        )
    }
